import { Popover as PopoverPrimitive } from 'radix-ui'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { Check as CheckIcon, MapPin as MapPinIcon, Xmark } from 'iconoir-react'
import { useState } from 'react'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { ChevronToggle } from 'app/components/ChevronToggle'
import NameIcon from 'app/components/shared/NameIcon'
import { useIsDarkMode } from 'app/hooks/useIsDarkMode'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import { isSMCOrg } from 'lib/isSMCOrg'
import type { UseGlobalWorkspaceSelectorReturn } from './useGlobalWorkspaceSelector'

export type GlobalWorkspaceSelectorComponentProps = UseGlobalWorkspaceSelectorReturn

export function GlobalWorkspaceSelectorComponent(props: GlobalWorkspaceSelectorComponentProps) {
  const {
    me,
    isOpen,
    setOpen,
    canPick,
    isOrgUser,
    selectedWorkspace,
    workspaces,
    searchValue,
    loading,
    initialLoading,
    orgOptions,
    inputRef,
    onWorkspaceChange,
    onSearchValueChange,
    onClearSearch,
    onOrgChange,
  } = props

  if (!me) return <></>

  const [buttonTooltipMessage, setButtonTooltipMessage] = useState<string>()
  const [activeWorkspaceMessage, setActiveWorkspaceMessage] = useState<string>()

  const displayName = selectedWorkspace?.name || `All Workspaces`
  const orgName = orgOptions.find((o) => o.id === me.orgId)?.name
  // Both light marks bake in a near-black plate, which shows as a seam on the black tile,
  // so dark uses the white glyph of each.
  const isDark = useIsDarkMode()
  const isMobile = useIsMobile()
  const trackableLogo = isDark ? `/public/trackable-glyph-white.svg` : `/public/trackable.png`
  const smcLogo = isDark ? `/public/smc-glyph-white.png` : `/public/smc_logo.png`

  return (
    <PopoverPrimitive.Root open={isOpen} onOpenChange={(open) => canPick && setOpen(open)}>
      <PopoverPrimitive.Trigger asChild>
        <button
          data-testid="workspace-selector-button"
          aria-label="All workspaces"
          className={classNames(
            `flex h-app-header w-full shrink-0 items-center gap-x-2 bg-sidebar-header pl-4 pr-5`,
            isOpen && canPick ? `cursor-pointer bg-(--fg-strong)/6` : ``,
            !isOpen && canPick ? `cursor-pointer hover:bg-(--fg-strong)/4` : `cursor-auto`,
          )}
        >
          <img
            src={isSMCOrg(me.orgId) ? smcLogo : trackableLogo}
            alt={isSMCOrg(me.orgId) ? `SMC logo` : `Trackable logo`}
            className="aspect-square max-h-8 max-w-8 shrink-0 rounded-lg bg-logo-plate px-[5px] pb-[7px] pt-1.5"
          />
          <div className="flex w-full flex-col justify-between truncate text-left">
            <Tooltip className={`flex truncate ${canPick ? `!cursor-pointer` : ``}`} message={buttonTooltipMessage}>
              <span
                className="truncate text-heading-xs text-strong"
                onMouseOver={(event) => {
                  if (
                    event.currentTarget instanceof HTMLElement &&
                    event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                  ) {
                    setButtonTooltipMessage(displayName)
                  } else setButtonTooltipMessage(undefined)
                }}
              >
                {displayName}
              </span>
            </Tooltip>
            <span className="text-label-xs text-subtle">{orgName}</span>
          </div>
          {canPick && <ChevronToggle className="shrink-0 pr-0" chevronClassName="h-5 w-5 text-subtle" open={isOpen} />}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={8}
          alignOffset={8}
          collisionPadding={12}
          className="z-50 flex max-h-[var(--radix-popover-content-available-height)] w-[400px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-md border border-default bg-surface-raised py-4 shadow-elevation-4"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search */}
          <div className="flex shrink-0 flex-col space-y-4 overflow-hidden px-4">
            <div className="flex h-8 w-full items-center gap-x-2 rounded-md border border-default bg-surface px-2 py-1 shadow-elevation-1">
              <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-default" />
              <input
                ref={inputRef}
                autoComplete="off"
                value={searchValue}
                placeholder="Search..."
                className="grow border-none p-0 text-base text-strong md:text-body-xs outline-none placeholder:text-minimal focus:outline-none focus:ring-0 focus:ring-offset-0"
                onChange={(event) => onSearchValueChange(event.target.value)}
                autoFocus={!isMobile}
              />
              {!loading && searchValue ? (
                <Xmark
                  className="h-4 w-4 shrink-0 cursor-pointer text-default"
                  onClick={(evt) => {
                    evt.stopPropagation()
                    onClearSearch()
                  }}
                />
              ) : (
                loading && (
                  <div role="status" className="flex items-center">
                    <Spinner />
                  </div>
                )
              )}
            </div>
            <span className="cursor-default pb-2 text-ui-xs leading-tight text-strong">Workspaces</span>
          </div>

          {/* Workspace list */}
          <div className="flex max-h-80 min-h-0 flex-col overflow-y-auto pb-3 [mask-image:linear-gradient(to_bottom,#000_calc(100%-12px),transparent)]">
            {initialLoading ? (
              <div className="flex items-center justify-center p-4">
                <Spinner size={4} />
              </div>
            ) : workspaces.length ? (
              workspaces.map((ws) => {
                const isSelected = ws.id === selectedWorkspace?.id
                // Picking a workspace in another org moves the session there, so the row names it.
                const otherOrgId = ws.meta?.orgId && ws.meta.orgId !== me.orgId ? ws.meta.orgId : undefined
                const otherOrgName = otherOrgId
                  ? orgOptions.find((o) => o.id === otherOrgId)?.name || `Other org`
                  : undefined
                return (
                  <div
                    key={ws.id}
                    className="flex cursor-pointer items-center px-3"
                    onClick={() => onWorkspaceChange(ws)}
                  >
                    <div className="flex w-full items-center justify-between space-x-2 rounded-md px-1 py-2.5 hover:bg-surface-strong md:py-1">
                      <div className="flex min-w-0 flex-1 items-center space-x-2">
                        <NameIcon
                          fullName={ws.name[0]?.toUpperCase()}
                          heightClassName="h-5"
                          widthClassName="w-5"
                          backgroundClassName="bg-fill-neutral-strong"
                          textClassName="text-caption-sm font-semibold text-on-color"
                          isTiny={false}
                        />
                        <Tooltip
                          className="flex h-4 min-w-0 !cursor-pointer items-center"
                          message={activeWorkspaceMessage}
                          onMouseOver={(event) => {
                            if (
                              event.currentTarget instanceof HTMLElement &&
                              event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                            ) {
                              setActiveWorkspaceMessage(ws.name)
                            } else setActiveWorkspaceMessage(undefined)
                          }}
                        >
                          <span className={classNames(`truncate text-body-xs`, isSelected && `font-medium`)}>
                            {ws.name}
                          </span>
                        </Tooltip>
                        {(ws?.meta?.locationMatches || otherOrgName) && (
                          <div className="ml-auto flex shrink-0 items-center gap-x-2">
                            {ws?.meta?.locationMatches && (
                              <Badge label="match" color="gray" icon={<MapPinIcon height={12} width={12} />} />
                            )}
                            {otherOrgId && (
                              <img
                                src={isSMCOrg(otherOrgId) ? smcLogo : trackableLogo}
                                alt={otherOrgName}
                                title={otherOrgName}
                                className="aspect-square h-5 w-5 shrink-0 rounded-md bg-logo-plate p-1"
                              />
                            )}
                          </div>
                        )}
                      </div>
                      {isSelected && <CheckIcon height={16} width={16} className="min-w-4 text-default" />}
                    </div>
                  </div>
                )
              })
            ) : (
              <span className="p-1 px-4 text-body-xs italic">No results found</span>
            )}
          </div>

          {/* Org selector */}
          {isOrgUser && (
            <>
              <hr className="mb-3 mt-1 shrink-0 border-default" />
              <div className="flex shrink-0 items-center justify-between gap-x-2 px-4">
                <span className="cursor-default text-ui-xs leading-tight text-strong">Organizations</span>
                <div className="flex items-center gap-x-1">
                  {orgOptions
                    .filter((org) => me.type === `super` || me.orgId === org.id)
                    .map((org) => (
                      <Button
                        key={org.id}
                        data-testid={`workspace-selector-org-${org.id}`}
                        variant="subtle"
                        size="icon-sm"
                        aria-pressed={org.id === me.orgId}
                        aria-label={org.name}
                        title={org.name}
                        className="ring-primary-600 aria-pressed:ring-2"
                        onClick={() => onOrgChange(org)}
                      >
                        <img
                          src={org.name === `SMC` ? smcLogo : trackableLogo}
                          alt={`${org.name} logo`}
                          className="aspect-square h-6 w-6 shrink-0 rounded-md bg-logo-plate p-[5px]"
                        />
                      </Button>
                    ))}
                </div>
              </div>
            </>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
